import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// TODO: LIMITATION - Offline queue limits as per PRD
const MAX_QUEUED_PHOTOS = 5;
const MAX_QUEUED_FORMS = 10;

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [pendingForms, setPendingForms] = useState([]);
  const [syncStatus, setSyncStatus] = useState('synced'); // 'synced' | 'pending' | 'error'
  const [syncError, setSyncError] = useState(null);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // TODO: FUTURE - Trigger automatic sync when coming back online
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update sync status based on queues
  useEffect(() => {
    if (pendingPhotos.length > 0 || pendingForms.length > 0) {
      setSyncStatus('pending');
    } else {
      setSyncStatus('synced');
    }
  }, [pendingPhotos, pendingForms]);

  // Queue a photo for upload
  const queuePhoto = useCallback((photoData) => {
    if (pendingPhotos.length >= MAX_QUEUED_PHOTOS) {
      return {
        success: false,
        error: `Upload limit reached. Please connect to WiFi to sync ${pendingPhotos.length} pending photos before adding more.`
      };
    }

    setPendingPhotos(prev => [...prev, {
      id: Date.now(),
      ...photoData,
      queuedAt: new Date().toISOString()
    }]);

    return { success: true };
  }, [pendingPhotos]);

  // Queue a form submission
  const queueForm = useCallback((formData) => {
    if (pendingForms.length >= MAX_QUEUED_FORMS) {
      return {
        success: false,
        error: `Upload limit reached. Please connect to WiFi to sync ${pendingForms.length} pending forms before adding more.`
      };
    }

    setPendingForms(prev => [...prev, {
      id: Date.now(),
      ...formData,
      queuedAt: new Date().toISOString()
    }]);

    return { success: true };
  }, [pendingForms]);

  // Remove item from photo queue after successful sync
  const removeFromPhotoQueue = useCallback((id) => {
    setPendingPhotos(prev => prev.filter(p => p.id !== id));
  }, []);

  // Remove item from form queue after successful sync
  const removeFromFormQueue = useCallback((id) => {
    setPendingForms(prev => prev.filter(f => f.id !== id));
  }, []);

  // Clear all queues (use after successful full sync)
  const clearQueues = useCallback(() => {
    setPendingPhotos([]);
    setPendingForms([]);
    setSyncStatus('synced');
    setSyncError(null);
  }, []);

  // Set sync error
  const setError = useCallback((error) => {
    setSyncError(error);
    setSyncStatus('error');
  }, []);

  const value = {
    isOnline,
    pendingPhotos,
    pendingForms,
    pendingPhotoCount: pendingPhotos.length,
    pendingFormCount: pendingForms.length,
    maxPhotos: MAX_QUEUED_PHOTOS,
    maxForms: MAX_QUEUED_FORMS,
    syncStatus,
    syncError,
    canQueuePhoto: pendingPhotos.length < MAX_QUEUED_PHOTOS,
    canQueueForm: pendingForms.length < MAX_QUEUED_FORMS,
    queuePhoto,
    queueForm,
    removeFromPhotoQueue,
    removeFromFormQueue,
    clearQueues,
    setError,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
